import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { authFetch, formatApiError } from '../utils/api';
import { ArrowLeft, Plus, Pencil, Trash2, LogOut, Mail, RefreshCw, Download, Play, ShieldAlert, Ban } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import AdminContentTab from '../components/AdminContentTab';

const txt = {
  en: {
    title: 'Admin Dashboard', back: 'Home', logout: 'Log Out',
    tabs: { pricing: 'Pricing Plans', faq: 'FAQ', messages: 'Contact Messages', content: 'Site Content' },
    addPlan: 'Add Plan', addFaq: 'Add FAQ', edit: 'Edit', delete: 'Delete', save: 'Save', cancel: 'Cancel',
    confirmDelete: 'Are you sure you want to delete this item?',
    refresh: 'Refresh', noMessages: 'No contact messages yet.', loading: 'Loading...',
    exportCsv: 'Export CSV', exportSuccess: 'CSV downloaded', exportError: 'Export failed', exportEmpty: 'No messages to export',
    drip: {
      title: 'Drip Campaign Stats',
      verified: 'Verified users',
      day0: 'Day 0 sent',
      day3: 'Day 3 sent',
      day7: 'Day 7 sent',
      unsubscribed: 'Unsubscribed',
      runNow: 'Run drip check now',
      runSuccess: 'Drip check complete',
    },
    security: {
      title: 'Recent lockout events (last 24h)',
      empty: 'No lockout events in the last 24 hours.',
      colUser: 'Account',
      colIp: 'IP',
      colAttempts: 'Attempts',
      colLastAttempt: 'Last attempt',
      colLockedUntil: 'Locked until',
      active: 'ACTIVE',
      expired: 'expired',
      block: 'Block',
      blocksTitle: 'Currently blocked IPs',
      blocksEmpty: 'No IPs are currently blocked.',
      colReason: 'Reason',
      colDuration: 'Duration',
      colBlockedBy: 'By',
      colExpires: 'Expires',
      permanent: 'PERMANENT',
      unblock: 'Unblock',
      confirmUnblock: 'Unblock this IP?',
      unblockSuccess: 'IP unblocked',
      blockDialogTitle: 'Block IP',
      blockDialogDesc: 'This IP will be denied login access. Manual blocks return HTTP 403 (distinct from the 429 rate-limiter).',
      fieldIp: 'IP address',
      fieldReason: 'Reason (visible to security audit log)',
      fieldDuration: 'Duration',
      submitBlock: 'Block IP',
      blockSuccess: 'IP blocked',
      yourIp: 'Your current IP',
      whitelisted: '(whitelisted)',
      selfBlockError: 'You cannot block your own current IP.',
    },
    fields: {
      name_en: 'Name (EN)', name_el: 'Name (EL)',
      description_en: 'Description (EN)', description_el: 'Description (EL)',
      monthly_price: 'Monthly Price (€)', yearly_price: 'Yearly Price (€)',
      price_label_en: 'Price Label (EN)', price_label_el: 'Price Label (EL)',
      features_en: 'Features (EN) — one per line', features_el: 'Features (EL) — one per line',
      cta_en: 'CTA (EN)', cta_el: 'CTA (EL)',
      popular_label_en: 'Popular Label (EN)', popular_label_el: 'Popular Label (EL)',
      popular: 'Mark as Popular', active: 'Active', order: 'Order',
      question_en: 'Question (EN)', question_el: 'Question (EL)',
      answer_en: 'Answer (EN)', answer_el: 'Answer (EL)',
    },
    notAdmin: 'You must be an admin to access this page.',
    saveSuccess: 'Saved successfully', deleteSuccess: 'Deleted', error: 'Operation failed',
  },
  el: {
    title: 'Πίνακας Διαχείρισης', back: 'Αρχική', logout: 'Αποσύνδεση',
    tabs: { pricing: 'Πλάνα Τιμολόγησης', faq: 'Συχνές Ερωτήσεις', messages: 'Μηνύματα Επικοινωνίας', content: 'Περιεχόμενο Site' },
    addPlan: 'Προσθήκη Πλάνου', addFaq: 'Προσθήκη Ερώτησης', edit: 'Επεξεργασία', delete: 'Διαγραφή', save: 'Αποθήκευση', cancel: 'Ακύρωση',
    confirmDelete: 'Είστε βέβαιοι ότι θέλετε να διαγράψετε;',
    refresh: 'Ανανέωση', noMessages: 'Δεν υπάρχουν μηνύματα.', loading: 'Φόρτωση...',
    exportCsv: 'Εξαγωγή CSV', exportSuccess: 'Το CSV κατέβηκε', exportError: 'Η εξαγωγή απέτυχε', exportEmpty: 'Δεν υπάρχουν μηνύματα προς εξαγωγή',
    drip: {
      title: 'Στατιστικά Drip Campaign',
      verified: 'Επαληθευμένοι χρήστες',
      day0: 'Day 0 εστάλησαν',
      day3: 'Day 3 εστάλησαν',
      day7: 'Day 7 εστάλησαν',
      unsubscribed: 'Απεγγραφές',
      runNow: 'Εκτέλεση drip check τώρα',
      runSuccess: 'Ο έλεγχος drip ολοκληρώθηκε',
    },
    security: {
      title: 'Πρόσφατα lockout events (τελευταίες 24ω)',
      empty: 'Δεν υπάρχουν lockout events τις τελευταίες 24 ώρες.',
      colUser: 'Λογαριασμός',
      colIp: 'IP',
      colAttempts: 'Προσπάθειες',
      colLastAttempt: 'Τελευταία προσπάθεια',
      colLockedUntil: 'Κλείδωμα έως',
      active: 'ΕΝΕΡΓΟ',
      expired: 'έληξε',
      block: 'Block',
      blocksTitle: 'IPs που είναι μπλοκαρισμένα',
      blocksEmpty: 'Δεν υπάρχει κανένα ενεργό block IP.',
      colReason: 'Αιτία',
      colDuration: 'Διάρκεια',
      colBlockedBy: 'Από',
      colExpires: 'Λήγει',
      permanent: 'ΜΟΝΙΜΟ',
      unblock: 'Unblock',
      confirmUnblock: 'Να γίνει unblock αυτό το IP;',
      unblockSuccess: 'Το IP έγινε unblock',
      blockDialogTitle: 'Μπλοκάρισμα IP',
      blockDialogDesc: 'Αυτό το IP δεν θα μπορεί να συνδεθεί. Τα manual blocks επιστρέφουν HTTP 403 (διαφορετικό από το 429 του rate-limiter).',
      fieldIp: 'Διεύθυνση IP',
      fieldReason: 'Αιτία (αποθηκεύεται στο security audit log)',
      fieldDuration: 'Διάρκεια',
      submitBlock: 'Μπλοκάρισμα IP',
      blockSuccess: 'Το IP μπλοκαρίστηκε',
      yourIp: 'Το τρέχον IP σας',
      whitelisted: '(στη whitelist)',
      selfBlockError: 'Δεν μπορείτε να μπλοκάρετε το δικό σας IP.',
    },
    fields: {
      name_en: 'Όνομα (EN)', name_el: 'Όνομα (EL)',
      description_en: 'Περιγραφή (EN)', description_el: 'Περιγραφή (EL)',
      monthly_price: 'Μηνιαία Τιμή (€)', yearly_price: 'Ετήσια Τιμή (€)',
      price_label_en: 'Ετικέτα Τιμής (EN)', price_label_el: 'Ετικέτα Τιμής (EL)',
      features_en: 'Χαρακτηριστικά (EN) — ένα ανά γραμμή', features_el: 'Χαρακτηριστικά (EL) — ένα ανά γραμμή',
      cta_en: 'CTA (EN)', cta_el: 'CTA (EL)',
      popular_label_en: 'Popular Label (EN)', popular_label_el: 'Popular Label (EL)',
      popular: 'Σήμανση ως Popular', active: 'Ενεργό', order: 'Σειρά',
      question_en: 'Ερώτηση (EN)', question_el: 'Ερώτηση (EL)',
      answer_en: 'Απάντηση (EN)', answer_el: 'Απάντηση (EL)',
    },
    notAdmin: 'Πρέπει να είστε διαχειριστής για να δείτε αυτή τη σελίδα.',
    saveSuccess: 'Αποθηκεύτηκε', deleteSuccess: 'Διαγράφηκε', error: 'Αποτυχία',
  }
};

const inputCls = "bg-white/5 border-white/10 text-white placeholder-white/40 focus-visible:ring-cyan-400/30";
const textareaCls = "w-full bg-white/5 border border-white/10 rounded-md p-2 text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-cyan-400/30";

const emptyPlan = {
  name_en: '', name_el: '', description_en: '', description_el: '',
  monthly_price: '', yearly_price: '', price_label_en: '', price_label_el: '',
  features_en: '', features_el: '',
  cta_en: 'Subscribe', cta_el: 'Εγγραφή',
  popular_label_en: '', popular_label_el: '',
  active: true, popular: false, order: 0
};

const emptyFaq = { question_en: '', question_el: '', answer_en: '', answer_el: '', order: 0 };

const planToForm = (p) => ({
  ...p,
  monthly_price: p.monthly_price ?? '',
  yearly_price: p.yearly_price ?? '',
  price_label_en: p.price_label_en ?? '',
  price_label_el: p.price_label_el ?? '',
  popular_label_en: p.popular_label_en ?? '',
  popular_label_el: p.popular_label_el ?? '',
  features_en: (p.features_en ?? []).join('\n'),
  features_el: (p.features_el ?? []).join('\n'),
});

const formToPlanPayload = (f) => ({
  name_en: f.name_en, name_el: f.name_el,
  description_en: f.description_en, description_el: f.description_el,
  monthly_price: f.monthly_price === '' ? null : Number(f.monthly_price),
  yearly_price: f.yearly_price === '' ? null : Number(f.yearly_price),
  price_label_en: f.price_label_en === '' ? null : f.price_label_en,
  price_label_el: f.price_label_el === '' ? null : f.price_label_el,
  features_en: f.features_en.split('\n').map((s) => s.trim()).filter(Boolean),
  features_el: f.features_el.split('\n').map((s) => s.trim()).filter(Boolean),
  cta_en: f.cta_en, cta_el: f.cta_el,
  popular_label_en: f.popular_label_en === '' ? null : f.popular_label_en,
  popular_label_el: f.popular_label_el === '' ? null : f.popular_label_el,
  active: !!f.active, popular: !!f.popular, order: Number(f.order) || 0
});

export default function AdminPage() {
  const { user, loading, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const T = txt[language] || txt.en;

  const [plans, setPlans] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dripStats, setDripStats] = useState(null);
  const [lockouts, setLockouts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [durationOptions, setDurationOptions] = useState(['1h', '24h', '7d', 'permanent']);
  const [whoami, setWhoami] = useState({ caller_ip: '', whitelisted: false });
  const [stripeHealth, setStripeHealth] = useState(null);
  const [blockDialog, setBlockDialog] = useState({ open: false, ip: '', reason: '', duration: '24h' });
  const [busy, setBusy] = useState(false);

  const [planDialog, setPlanDialog] = useState({ open: false, mode: 'create', form: emptyPlan, id: null });
  const [faqDialog, setFaqDialog] = useState({ open: false, mode: 'create', form: emptyFaq, id: null });

  const fetchAll = useCallback(async () => {
    setBusy(true);
    try {
      const [pRes, fRes, mRes, dRes, sRes, bRes, wRes, hRes] = await Promise.all([
        authFetch('/api/pricing/all'),
        authFetch('/api/faq'),
        authFetch('/api/admin/contact/messages'),
        authFetch('/api/admin/drip/stats'),
        authFetch('/api/admin/security/lockouts'),
        authFetch('/api/admin/security/blocks'),
        authFetch('/api/admin/security/whoami'),
        authFetch('/api/admin/stripe/health'),
      ]);
      if (pRes.ok) setPlans(await pRes.json());
      if (fRes.ok) setFaqs(await fRes.json());
      if (mRes.ok) setMessages(await mRes.json());
      if (dRes.ok) setDripStats(await dRes.json());
      if (sRes.ok) {
        const data = await sRes.json();
        setLockouts(data.events || []);
      }
      if (bRes.ok) {
        const data = await bRes.json();
        setBlocks(data.blocks || []);
        if (data.duration_options) setDurationOptions(data.duration_options);
      }
      if (wRes.ok) setWhoami(await wRes.json());
      if (hRes.ok) setStripeHealth(await hRes.json());
    } catch (e) {
      toast.error(T.error);
    } finally {
      setBusy(false);
    }
  }, [T.error]);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchAll();
  }, [user, loading, navigate, fetchAll]);

  if (loading || !user) {
    return <div className="min-h-screen bg-[#050811] flex items-center justify-center text-white/60">{T.loading}</div>;
  }
  if (user.role !== 'admin') {
    return <div className="min-h-screen bg-[#050811] flex items-center justify-center text-white">{T.notAdmin}</div>;
  }

  const openCreatePlan = () => setPlanDialog({ open: true, mode: 'create', form: emptyPlan, id: null });
  const openEditPlan = (plan) => setPlanDialog({ open: true, mode: 'edit', form: planToForm(plan), id: plan.id });

  const savePlan = async () => {
    const payload = formToPlanPayload(planDialog.form);
    const isEdit = planDialog.mode === 'edit';
    const url = isEdit ? `/api/admin/pricing/${planDialog.id}` : '/api/admin/pricing';
    const method = isEdit ? 'PUT' : 'POST';
    try {
      const res = await authFetch(url, { method, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw data;
      toast.success(T.saveSuccess);
      setPlanDialog({ ...planDialog, open: false });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.detail) || T.error);
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm(T.confirmDelete)) return;
    try {
      const res = await authFetch(`/api/admin/pricing/${id}`, { method: 'DELETE' });
      if (!res.ok) throw await res.json();
      toast.success(T.deleteSuccess);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.detail) || T.error);
    }
  };

  const openCreateFaq = () => setFaqDialog({ open: true, mode: 'create', form: emptyFaq, id: null });
  const openEditFaq = (faq) => setFaqDialog({ open: true, mode: 'edit', form: { ...faq }, id: faq.id });

  const saveFaq = async () => {
    const isEdit = faqDialog.mode === 'edit';
    const url = isEdit ? `/api/admin/faq/${faqDialog.id}` : '/api/admin/faq';
    const method = isEdit ? 'PUT' : 'POST';
    const payload = { ...faqDialog.form, order: Number(faqDialog.form.order) || 0 };
    try {
      const res = await authFetch(url, { method, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw data;
      toast.success(T.saveSuccess);
      setFaqDialog({ ...faqDialog, open: false });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.detail) || T.error);
    }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm(T.confirmDelete)) return;
    try {
      const res = await authFetch(`/api/admin/faq/${id}`, { method: 'DELETE' });
      if (!res.ok) throw await res.json();
      toast.success(T.deleteSuccess);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.detail) || T.error);
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm(T.confirmDelete)) return;
    try {
      const res = await authFetch(`/api/admin/contact/messages/${id}`, { method: 'DELETE' });
      if (!res.ok) throw await res.json();
      toast.success(T.deleteSuccess);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.detail) || T.error);
    }
  };

  const exportMessagesCsv = async () => {
    if (messages.length === 0) {
      toast.error(T.exportEmpty);
      return;
    }
    try {
      const res = await authFetch('/api/admin/contact/messages/export');
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const today = new Date().toISOString().slice(0, 10);
      const filename = match ? match[1] : `contact_messages_${today}.csv`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(T.exportSuccess);
    } catch {
      toast.error(T.exportError);
    }
  };

  const runDripCheck = async () => {
    try {
      const res = await authFetch('/api/admin/drip/run', { method: 'POST' });
      if (!res.ok) throw new Error('drip run failed');
      const data = await res.json();
      toast.success(`${T.drip.runSuccess} (day3=${data.day3}, day7=${data.day7})`);
      fetchAll();
    } catch {
      toast.error(T.error);
    }
  };

  const openBlockDialog = (ip = '') => {
    if (ip && whoami.caller_ip && ip === whoami.caller_ip) {
      toast.error(T.security.selfBlockError);
      return;
    }
    setBlockDialog({ open: true, ip, reason: '', duration: '24h' });
  };

  const submitBlock = async () => {
    const { ip, reason, duration } = blockDialog;
    try {
      const res = await authFetch('/api/admin/security/block-ip', {
        method: 'POST',
        body: JSON.stringify({ ip: ip.trim(), reason: reason.trim(), duration }),
      });
      const data = await res.json();
      if (!res.ok) throw data;
      toast.success(`${T.security.blockSuccess}: ${ip}`);
      setBlockDialog({ open: false, ip: '', reason: '', duration: '24h' });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.detail) || T.error);
    }
  };

  const handleUnblock = async (ip) => {
    if (!window.confirm(T.security.confirmUnblock)) return;
    try {
      const res = await authFetch('/api/admin/security/unblock-ip', {
        method: 'POST',
        body: JSON.stringify({ ip }),
      });
      if (!res.ok) throw await res.json();
      toast.success(`${T.security.unblockSuccess}: ${ip}`);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err?.detail) || T.error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#050811] text-white">
      <Toaster theme="dark" position="top-right" />
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 z-10 bg-[#050811]/95 backdrop-blur">
        <div className="flex items-center gap-4">
          <Link to="/" className="inline-flex items-center text-white/60 hover:text-white text-sm transition-colors" data-testid="admin-back-home">
            <ArrowLeft size={16} className="mr-2" /> {T.back}
          </Link>
          <h1 className="text-xl font-semibold" data-testid="admin-title">{T.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={fetchAll} disabled={busy} className="text-white/70 hover:text-white" data-testid="admin-refresh">
            <RefreshCw size={16} className={`mr-2 ${busy ? 'animate-spin' : ''}`} /> {T.refresh}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white" data-testid="admin-logout">
            <LogOut size={16} className="mr-2" /> {T.logout}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Drip campaign stats widget */}
        {dripStats && (
          <div
            className="bg-gradient-to-br from-cyan-500/5 to-blue-500/5 border border-white/10 rounded-xl p-5 mb-8"
            data-testid="admin-drip-widget"
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-lg font-semibold text-white/90">{T.drip.title}</h2>
              <Button
                onClick={runDripCheck}
                size="sm"
                variant="outline"
                className="border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 hover:text-cyan-200"
                data-testid="admin-drip-run"
              >
                <Play size={14} className="mr-2" /> {T.drip.runNow}
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                ['verified_users', T.drip.verified, 'text-white'],
                ['day0_sent', T.drip.day0, 'text-emerald-400'],
                ['day3_sent', T.drip.day3, 'text-cyan-400'],
                ['day7_sent', T.drip.day7, 'text-blue-400'],
                ['unsubscribed', T.drip.unsubscribed, 'text-orange-400'],
              ].map(([key, label, color]) => (
                <div
                  key={key}
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3"
                  data-testid={`drip-stat-${key}`}
                >
                  <p className="text-white/50 text-xs uppercase tracking-wide mb-1">{label}</p>
                  <p className={`text-2xl font-semibold ${color}`}>{dripStats[key] ?? 0}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stripe health widget */}
        {stripeHealth && (
          <div
            className="bg-gradient-to-br from-purple-500/5 to-blue-500/5 border border-white/10 rounded-xl p-5 mb-8"
            data-testid="admin-stripe-health-widget"
          >
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-purple-400 text-lg">💳</span>
              <h2 className="text-lg font-semibold text-white/90">Stripe Integration Health</h2>
              <span className={`ml-auto text-xs px-2 py-1 rounded-full font-semibold uppercase tracking-wide ${
                stripeHealth.mode === 'live'
                  ? 'bg-red-500/20 text-red-300'
                  : stripeHealth.mode === 'test'
                    ? 'bg-amber-500/20 text-amber-300'
                    : 'bg-white/10 text-white/60'
              }`} data-testid="admin-stripe-mode">
                {stripeHealth.mode}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                <p className="text-white/50 text-xs uppercase tracking-wide mb-1">API key</p>
                <p className={stripeHealth.stripe_configured ? 'text-emerald-400' : 'text-red-400'} data-testid="admin-stripe-configured">
                  {stripeHealth.stripe_configured ? '✓ Configured' : '✗ Missing'}
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                <p className="text-white/50 text-xs uppercase tracking-wide mb-1">Webhook signing secret</p>
                <p className={stripeHealth.webhook_secret_configured ? 'text-emerald-400' : 'text-amber-400'} data-testid="admin-stripe-webhook-secret">
                  {stripeHealth.webhook_secret_configured ? '✓ Configured' : '⚠ Not set — required for live'}
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 md:col-span-2">
                <p className="text-white/50 text-xs uppercase tracking-wide mb-1">Webhook URL (register in Stripe Dashboard)</p>
                <p className="text-cyan-300 font-mono text-xs break-all" data-testid="admin-stripe-webhook-url">
                  {stripeHealth.webhook_url}
                </p>
              </div>
              {stripeHealth.last_webhook_received_at && (
                <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 md:col-span-2">
                  <p className="text-white/50 text-xs uppercase tracking-wide mb-1">Last webhook received</p>
                  <p className="text-white/80 text-xs" data-testid="admin-stripe-last-webhook">
                    {new Date(stripeHealth.last_webhook_received_at).toLocaleString()}
                    {' · '}
                    <span className="text-cyan-300">{stripeHealth.last_webhook_event_type}</span>
                    {' → '}
                    <span className={`font-semibold ${
                      stripeHealth.last_webhook_status === 'duplicate'
                        ? 'text-amber-400'
                        : stripeHealth.last_webhook_status?.startsWith('error')
                          ? 'text-red-400'
                          : 'text-emerald-400'
                    }`}>
                      {stripeHealth.last_webhook_status}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent lockout events widget */}
        <div
          className="bg-gradient-to-br from-red-500/5 to-orange-500/5 border border-white/10 rounded-xl p-5 mb-8"
          data-testid="admin-security-widget"
        >
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <ShieldAlert size={18} className="text-red-400" />
            <h2 className="text-lg font-semibold text-white/90">{T.security.title}</h2>
            <span className="ml-auto text-white/40 text-xs uppercase tracking-wide">{lockouts.length} events</span>
          </div>
          {whoami.caller_ip && (
            <p className="text-white/40 text-xs mb-3" data-testid="admin-security-whoami">
              {T.security.yourIp}: <span className="font-mono">{whoami.caller_ip}</span>
              {whoami.whitelisted && <span className="ml-2 text-emerald-400">{T.security.whitelisted}</span>}
            </p>
          )}
          {lockouts.length === 0 ? (
            <p className="text-white/50 text-sm" data-testid="admin-security-empty">{T.security.empty}</p>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/50 text-xs uppercase tracking-wide">
                    <th className="text-left px-2 py-2 font-normal">{T.security.colUser}</th>
                    <th className="text-left px-2 py-2 font-normal">{T.security.colIp}</th>
                    <th className="text-left px-2 py-2 font-normal">{T.security.colAttempts}</th>
                    <th className="text-left px-2 py-2 font-normal">{T.security.colLastAttempt}</th>
                    <th className="text-left px-2 py-2 font-normal">{T.security.colLockedUntil}</th>
                    <th className="text-left px-2 py-2 font-normal"></th>
                  </tr>
                </thead>
                <tbody>
                  {lockouts.map((ev) => {
                    const key = `${ev.ip}:${ev.email}:${ev.last_attempt}`;
                    const lockedUntilDate = ev.locked_until ? new Date(ev.locked_until) : null;
                    const isActive = lockedUntilDate && lockedUntilDate > new Date();
                    const alreadyBlocked = blocks.some((b) => b.ip === ev.ip);
                    return (
                      <tr key={key} className="border-t border-white/5" data-testid={`admin-security-row-${ev.email}`}>
                        <td className="px-2 py-2 text-white/90 break-all">{ev.email}</td>
                        <td className="px-2 py-2 text-white/70 font-mono text-xs">{ev.ip}</td>
                        <td className="px-2 py-2 text-white/90">{ev.attempts}</td>
                        <td className="px-2 py-2 text-white/60 text-xs">{ev.last_attempt ? new Date(ev.last_attempt).toLocaleString() : '—'}</td>
                        <td className="px-2 py-2 text-xs">
                          {lockedUntilDate ? (
                            <span className={isActive ? 'text-red-400 font-semibold' : 'text-white/40'}>
                              {isActive ? `${T.security.active} · ${lockedUntilDate.toLocaleString()}` : `${T.security.expired}: ${lockedUntilDate.toLocaleString()}`}
                            </span>
                          ) : (
                            <span className="text-white/40">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={alreadyBlocked}
                            onClick={() => openBlockDialog(ev.ip)}
                            className="border-red-400/40 text-red-300 hover:bg-red-400/10 hover:text-red-200 h-7 text-xs"
                            data-testid={`admin-security-block-${ev.email}`}
                          >
                            <Ban size={12} className="mr-1" /> {T.security.block}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Currently blocked IPs */}
          <div className="mt-6 pt-4 border-t border-white/10">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Ban size={16} className="text-red-400" />
              <h3 className="text-sm font-semibold text-white/80">{T.security.blocksTitle}</h3>
              <span className="ml-auto text-white/40 text-xs uppercase tracking-wide">{blocks.length}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openBlockDialog('')}
                className="border-red-400/40 text-red-300 hover:bg-red-400/10 hover:text-red-200 h-7 text-xs"
                data-testid="admin-security-add-block"
              >
                <Plus size={12} className="mr-1" /> {T.security.submitBlock}
              </Button>
            </div>
            {blocks.length === 0 ? (
              <p className="text-white/40 text-xs" data-testid="admin-security-blocks-empty">{T.security.blocksEmpty}</p>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/50 text-xs uppercase tracking-wide">
                      <th className="text-left px-2 py-2 font-normal">{T.security.colIp}</th>
                      <th className="text-left px-2 py-2 font-normal">{T.security.colReason}</th>
                      <th className="text-left px-2 py-2 font-normal">{T.security.colDuration}</th>
                      <th className="text-left px-2 py-2 font-normal">{T.security.colExpires}</th>
                      <th className="text-left px-2 py-2 font-normal">{T.security.colBlockedBy}</th>
                      <th className="text-left px-2 py-2 font-normal"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {blocks.map((b) => (
                      <tr key={b.ip} className="border-t border-white/5" data-testid={`admin-security-block-row-${b.ip}`}>
                        <td className="px-2 py-2 text-white/90 font-mono text-xs">{b.ip}</td>
                        <td className="px-2 py-2 text-white/70 text-xs">{b.reason}</td>
                        <td className="px-2 py-2 text-white/70 text-xs">{b.duration}</td>
                        <td className="px-2 py-2 text-xs">
                          {b.expires_at ? (
                            <span className="text-white/70">{new Date(b.expires_at).toLocaleString()}</span>
                          ) : (
                            <span className="text-red-400 font-semibold">{T.security.permanent}</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-white/50 text-xs break-all">{b.blocked_by}</td>
                        <td className="px-2 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnblock(b.ip)}
                            className="border-white/20 text-white/70 hover:bg-white/10 h-7 text-xs"
                            data-testid={`admin-security-unblock-${b.ip}`}
                          >
                            {T.security.unblock}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Block IP dialog */}
        <Dialog open={blockDialog.open} onOpenChange={(open) => setBlockDialog((prev) => ({ ...prev, open }))}>
          <DialogContent className="bg-[#0a0e27] border-white/10 text-white" data-testid="admin-security-block-dialog">
            <DialogHeader>
              <DialogTitle>{T.security.blockDialogTitle}</DialogTitle>
              <DialogDescription className="text-white/60 text-xs">{T.security.blockDialogDesc}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-white/60 text-xs mb-1 block">{T.security.fieldIp}</label>
                <Input
                  value={blockDialog.ip}
                  onChange={(e) => setBlockDialog((p) => ({ ...p, ip: e.target.value }))}
                  placeholder="203.0.113.1"
                  className="bg-white/5 border-white/10 font-mono"
                  data-testid="admin-security-dialog-ip"
                />
              </div>
              <div>
                <label className="text-white/60 text-xs mb-1 block">{T.security.fieldReason}</label>
                <Input
                  value={blockDialog.reason}
                  onChange={(e) => setBlockDialog((p) => ({ ...p, reason: e.target.value }))}
                  placeholder="Distributed brute force attack"
                  className="bg-white/5 border-white/10"
                  data-testid="admin-security-dialog-reason"
                />
              </div>
              <div>
                <label className="text-white/60 text-xs mb-1 block">{T.security.fieldDuration}</label>
                <select
                  value={blockDialog.duration}
                  onChange={(e) => setBlockDialog((p) => ({ ...p, duration: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white text-sm"
                  data-testid="admin-security-dialog-duration"
                >
                  {durationOptions.map((opt) => (
                    <option key={opt} value={opt} className="bg-[#0a0e27]">{opt}</option>
                  ))}
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setBlockDialog({ open: false, ip: '', reason: '', duration: '24h' })}
                className="text-white/70 hover:text-white"
              >
                {T.cancel}
              </Button>
              <Button
                onClick={submitBlock}
                disabled={!blockDialog.ip.trim()}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="admin-security-dialog-submit"
              >
                <Ban size={14} className="mr-2" /> {T.security.submitBlock}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Tabs defaultValue="pricing" className="w-full">
          {/* placeholder: security widget rendered above */}
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="pricing" data-testid="tab-pricing">{T.tabs.pricing}</TabsTrigger>
            <TabsTrigger value="faq" data-testid="tab-faq">{T.tabs.faq}</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">{T.tabs.content}</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">{T.tabs.messages}</TabsTrigger>
          </TabsList>

          <TabsContent value="pricing" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button onClick={openCreatePlan} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600" data-testid="add-plan-btn">
                <Plus size={16} className="mr-2" /> {T.addPlan}
              </Button>
            </div>
            <div className="grid gap-4" data-testid="admin-plans-list">
              {plans.map((p) => (
                <div key={p.id} className="bg-white/5 border border-white/10 rounded-lg p-5 flex items-start justify-between gap-4" data-testid={`admin-plan-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold">{p.name_en} / {p.name_el}</h3>
                      {p.popular && <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30">Popular</Badge>}
                      {!p.active && <Badge variant="secondary">Inactive</Badge>}
                      <Badge variant="outline" className="text-white/60 border-white/20">#{p.order}</Badge>
                    </div>
                    <p className="text-sm text-white/60 mb-2">
                      {p.monthly_price != null ? `€${p.monthly_price}/mo` : (p.price_label_en || '—')}
                      {p.yearly_price != null ? ` · €${p.yearly_price}/yr` : ''}
                    </p>
                    <p className="text-xs text-white/40">{p.features_en?.length || 0} features</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openEditPlan(p)} data-testid={`edit-plan-${p.id}`}>
                      <Pencil size={14} className="mr-1" /> {T.edit}
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => deletePlan(p.id)} data-testid={`delete-plan-${p.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="faq" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button onClick={openCreateFaq} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600" data-testid="add-faq-btn">
                <Plus size={16} className="mr-2" /> {T.addFaq}
              </Button>
            </div>
            <div className="grid gap-4" data-testid="admin-faq-list">
              {faqs.map((f) => (
                <div key={f.id} className="bg-white/5 border border-white/10 rounded-lg p-5 flex items-start justify-between gap-4" data-testid={`admin-faq-${f.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-white/60 border-white/20">#{f.order}</Badge>
                      <h3 className="text-base font-semibold truncate">{f.question_en}</h3>
                    </div>
                    <p className="text-sm text-white/50 truncate">{f.question_el}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openEditFaq(f)} data-testid={`edit-faq-${f.id}`}>
                      <Pencil size={14} className="mr-1" /> {T.edit}
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => deleteFaq(f.id)} data-testid={`delete-faq-${f.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="content" className="mt-6">
            <AdminContentTab T={T} language={language} />
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button
                onClick={exportMessagesCsv}
                disabled={messages.length === 0}
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="admin-messages-export-csv"
              >
                <Download size={16} className="mr-2" /> {T.exportCsv}
              </Button>
            </div>
            <div className="grid gap-4" data-testid="admin-messages-list">
              {messages.length === 0 && <p className="text-white/60">{T.noMessages}</p>}
              {messages.map((m) => (
                <div key={m.id} className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid={`admin-message-${m.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="min-w-0">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Mail size={16} className="text-cyan-400" />{m.name}
                      </h3>
                      <a href={`mailto:${m.email}`} className="text-sm text-cyan-400 hover:underline">{m.email}</a>
                      <p className="text-xs text-white/40 mt-1">{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => deleteMessage(m.id)} data-testid={`delete-message-${m.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  {m.subject && <p className="text-sm font-medium mb-2 text-white/80">Subject: {m.subject}</p>}
                  <p className="text-sm text-white/70 whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Pricing dialog */}
      <Dialog open={planDialog.open} onOpenChange={(v) => setPlanDialog({ ...planDialog, open: v })}>
        <DialogContent className="bg-[#0a0e27] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle>{planDialog.mode === 'edit' ? T.edit : T.addPlan}</DialogTitle>
            <DialogDescription className="text-white/60">{T.tabs.pricing}</DialogDescription>
          </DialogHeader>
          <PlanForm form={planDialog.form} onChange={(form) => setPlanDialog({ ...planDialog, form })} T={T} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPlanDialog({ ...planDialog, open: false })}>{T.cancel}</Button>
            <Button onClick={savePlan} className="bg-gradient-to-r from-cyan-500 to-blue-500" data-testid="plan-save-btn">{T.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FAQ dialog */}
      <Dialog open={faqDialog.open} onOpenChange={(v) => setFaqDialog({ ...faqDialog, open: v })}>
        <DialogContent className="bg-[#0a0e27] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="faq-dialog">
          <DialogHeader>
            <DialogTitle>{faqDialog.mode === 'edit' ? T.edit : T.addFaq}</DialogTitle>
            <DialogDescription className="text-white/60">{T.tabs.faq}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label={T.fields.question_en}>
              <Input className={inputCls} value={faqDialog.form.question_en} onChange={(e) => setFaqDialog({ ...faqDialog, form: { ...faqDialog.form, question_en: e.target.value } })} data-testid="faq-question-en" />
            </Field>
            <Field label={T.fields.question_el}>
              <Input className={inputCls} value={faqDialog.form.question_el} onChange={(e) => setFaqDialog({ ...faqDialog, form: { ...faqDialog.form, question_el: e.target.value } })} data-testid="faq-question-el" />
            </Field>
            <Field label={T.fields.answer_en}>
              <textarea rows={4} className={textareaCls} value={faqDialog.form.answer_en} onChange={(e) => setFaqDialog({ ...faqDialog, form: { ...faqDialog.form, answer_en: e.target.value } })} data-testid="faq-answer-en" />
            </Field>
            <Field label={T.fields.answer_el}>
              <textarea rows={4} className={textareaCls} value={faqDialog.form.answer_el} onChange={(e) => setFaqDialog({ ...faqDialog, form: { ...faqDialog.form, answer_el: e.target.value } })} data-testid="faq-answer-el" />
            </Field>
            <Field label={T.fields.order}>
              <Input type="number" className={inputCls} value={faqDialog.form.order} onChange={(e) => setFaqDialog({ ...faqDialog, form: { ...faqDialog.form, order: e.target.value } })} data-testid="faq-order" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFaqDialog({ ...faqDialog, open: false })}>{T.cancel}</Button>
            <Button onClick={saveFaq} className="bg-gradient-to-r from-cyan-500 to-blue-500" data-testid="faq-save-btn">{T.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-white/70 text-sm">{label}</Label>
    {children}
  </div>
);

const PlanForm = ({ form, onChange, T }) => {
  const upd = (k, v) => onChange({ ...form, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label={T.fields.name_en}><Input className={inputCls} value={form.name_en} onChange={(e) => upd('name_en', e.target.value)} data-testid="plan-name-en" /></Field>
      <Field label={T.fields.name_el}><Input className={inputCls} value={form.name_el} onChange={(e) => upd('name_el', e.target.value)} data-testid="plan-name-el" /></Field>
      <Field label={T.fields.description_en}><textarea rows={2} className={textareaCls} value={form.description_en} onChange={(e) => upd('description_en', e.target.value)} /></Field>
      <Field label={T.fields.description_el}><textarea rows={2} className={textareaCls} value={form.description_el} onChange={(e) => upd('description_el', e.target.value)} /></Field>
      <Field label={T.fields.monthly_price}><Input type="number" step="0.01" className={inputCls} value={form.monthly_price} onChange={(e) => upd('monthly_price', e.target.value)} data-testid="plan-monthly" /></Field>
      <Field label={T.fields.yearly_price}><Input type="number" step="0.01" className={inputCls} value={form.yearly_price} onChange={(e) => upd('yearly_price', e.target.value)} data-testid="plan-yearly" /></Field>
      <Field label={T.fields.price_label_en}><Input className={inputCls} value={form.price_label_en} onChange={(e) => upd('price_label_en', e.target.value)} /></Field>
      <Field label={T.fields.price_label_el}><Input className={inputCls} value={form.price_label_el} onChange={(e) => upd('price_label_el', e.target.value)} /></Field>
      <Field label={T.fields.features_en}><textarea rows={6} className={textareaCls} value={form.features_en} onChange={(e) => upd('features_en', e.target.value)} data-testid="plan-features-en" /></Field>
      <Field label={T.fields.features_el}><textarea rows={6} className={textareaCls} value={form.features_el} onChange={(e) => upd('features_el', e.target.value)} data-testid="plan-features-el" /></Field>
      <Field label={T.fields.cta_en}><Input className={inputCls} value={form.cta_en} onChange={(e) => upd('cta_en', e.target.value)} /></Field>
      <Field label={T.fields.cta_el}><Input className={inputCls} value={form.cta_el} onChange={(e) => upd('cta_el', e.target.value)} /></Field>
      <Field label={T.fields.popular_label_en}><Input className={inputCls} value={form.popular_label_en} onChange={(e) => upd('popular_label_en', e.target.value)} /></Field>
      <Field label={T.fields.popular_label_el}><Input className={inputCls} value={form.popular_label_el} onChange={(e) => upd('popular_label_el', e.target.value)} /></Field>
      <Field label={T.fields.order}><Input type="number" className={inputCls} value={form.order} onChange={(e) => upd('order', e.target.value)} /></Field>
      <div className="flex gap-6 items-end">
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={!!form.active} onCheckedChange={(v) => upd('active', v)} data-testid="plan-active" /> {T.fields.active}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={!!form.popular} onCheckedChange={(v) => upd('popular', v)} data-testid="plan-popular" /> {T.fields.popular}
        </label>
      </div>
    </div>
  );
};
